import { useRef } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import { ShoppingCartIcon, CaretRightIcon } from '@phosphor-icons/react';

import Heading from '@components/ui/Heading';
import LinkButton from '_components/UI/LinkButton';
import useObjectVisibility from '@hooks/useObjectVisibility';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';
import { trackMainHeroCtaClick } from '_utils/GA4/trackMainHeroEvents';
import { MainHeroData } from './types';

import styles from './MainHero.module.scss';

export interface MainHeroProps {
  data: MainHeroData;
}

/** Info card link is the third fixed CTA slot, after cta_1 (0) and cta_2 (1). */
const INFO_CARD_CTA_SLOT = 2;

const MainHero = ({ data }: MainHeroProps) => {
  const { h1, image, mobileImage, ctas, infoCard, animation } = data;
  const sectionRef = useRef<HTMLElement | null>(null);
  const revealed = useObjectVisibility(sectionRef);
  const isMobile = useBreakpoint(Breakpoints.md);
  const locale = useLocale();

  const desktopImageUrl = image?.url ?? mobileImage?.url ?? null;
  const mobileImageUrl = mobileImage?.url ?? image?.url ?? null;
  const hasDistinctImages =
    !!desktopImageUrl && !!mobileImageUrl && desktopImageUrl !== mobileImageUrl;
  const infoCardHref = infoCard ? localizeHref(infoCard.cta.url, locale) : null;

  return (
    <section
      ref={sectionRef}
      className={cn(styles.hero, revealed && styles.revealed)}
      data-animation={animation}
      data-testid="main-hero"
    >
      {desktopImageUrl && (
        <>
          <div className={styles.background}>
            <Image
              priority
              src={desktopImageUrl}
              alt={image?.alt ?? mobileImage?.alt ?? ''}
              fill
              sizes="100vw"
              className={cn(
                styles.backgroundImage,
                hasDistinctImages && styles.desktopImage
              )}
            />
            {hasDistinctImages && (
              <Image
                priority
                src={mobileImageUrl as string}
                alt={mobileImage?.alt ?? image?.alt ?? ''}
                fill
                sizes="100vw"
                className={cn(styles.backgroundImage, styles.mobileImage)}
              />
            )}
          </div>
          <div className={styles.overlay} aria-hidden="true" />
        </>
      )}
      <div className={styles.content}>
        {h1.trim() && (
          <Heading semanticLevel="h1" styleLevel="1" className={styles.title}>
            {h1}
          </Heading>
        )}
        {ctas.length > 0 && (
          <div className={styles.ctaRow}>
            {ctas.map(({ label, url, slot }) => {
              const href = localizeHref(url, locale);
              return (
                <LinkButton
                  key={`${url}-${slot}`}
                  href={href}
                  variant="white"
                  onClick={() =>
                    trackMainHeroCtaClick({
                      data: {
                        interactionLocation: window.location.pathname,
                        linkUrl: href,
                        linkText: label,
                        index: slot,
                      },
                    })
                  }
                >
                  {label}
                </LinkButton>
              );
            })}
          </div>
        )}
      </div>
      {infoCard && !isMobile && (
        <div className={styles.infoCard}>
          <p className={styles.infoCardDescription}>{infoCard.description}</p>
          <a
            href={infoCardHref as string}
            className={styles.infoCardCta}
            onClick={() =>
              trackMainHeroCtaClick({
                data: {
                  interactionLocation: window.location.pathname,
                  linkUrl: infoCardHref as string,
                  linkText: infoCard.cta.label,
                  index: INFO_CARD_CTA_SLOT,
                },
              })
            }
          >
            <ShoppingCartIcon size={16} aria-hidden="true" />
            <span>{infoCard.cta.label}</span>
            <CaretRightIcon size={8} aria-hidden="true" />
          </a>
        </div>
      )}
    </section>
  );
};

export default MainHero;
